import React, { memo, useCallback, useMemo } from 'react';
import type { OutlineTreeNode } from './canvasOutlineTree';

const KIND_ACCENTS: Record<
    OutlineTreeNode['kind'],
    { icon: string; labelClass: string }
> = {
    user_flow: {
        icon: 'text-[#34d399]',
        labelClass: 'text-emerald-200/95',
    },
    hlr: {
        icon: 'text-[#60a5fa]',
        labelClass: 'text-blue-200/95',
    },
    llr: {
        icon: 'text-[#c4b5fd]',
        labelClass: 'text-violet-200/95',
    },
    test_case: {
        icon: 'text-[#a5b4fc]',
        labelClass: 'text-indigo-100/95',
    },
};

function HierarchyIcon({ className }: { className: string }) {
    return (
        <svg
            className={`h-3.5 w-3.5 shrink-0 ${className}`}
            viewBox="0 0 16 16"
            fill="currentColor"
            aria-hidden
        >
            <path d="M2 3.5h4v2H4v7H2v-9zm8 0H6v2h4v-2zm2 2h2v7h-4V7h2V5.5zM8 8H6v5h6V8H8z" />
        </svg>
    );
}

interface OutlineRowProps {
    node: OutlineTreeNode;
    depth: number;
    expandedIds: ReadonlySet<string>;
    selectedId: string | null;
    onToggleExpand: (id: string) => void;
    onSelectFocus: (node: OutlineTreeNode) => void;
}

const OutlineRow = memo(function OutlineRow({
    node,
    depth,
    expandedIds,
    selectedId,
    onToggleExpand,
    onSelectFocus,
}: OutlineRowProps) {
    const expanded = expandedIds.has(node.id);
    const hasChildren = node.children.length > 0;
    const accent = KIND_ACCENTS[node.kind];
    const isSelected = selectedId === node.id;

    const onChevronClick = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            if (!hasChildren) return;
            onToggleExpand(node.id);
        },
        [hasChildren, node.id, onToggleExpand]
    );

    const onRowClick = useCallback(() => {
        if (hasChildren) {
            onToggleExpand(node.id);
        }
        onSelectFocus(node);
    }, [hasChildren, node, onSelectFocus, onToggleExpand]);

    return (
        <div role="treeitem" aria-expanded={hasChildren ? expanded : undefined}>
            <div
                className={`
                    flex cursor-pointer items-center gap-1 rounded py-1 pr-1 text-left text-[13px] leading-snug
                    ${isSelected ? 'bg-white/[0.18]' : 'hover:bg-white/[0.12]'}
                `}
                style={{ paddingLeft: 6 + depth * 14 }}
                onClick={onRowClick}
            >
                <button
                    type="button"
                    aria-label={expanded ? 'Collapse' : 'Expand'}
                    className={`
                        flex h-6 w-5 shrink-0 items-center justify-center rounded text-zinc-400
                        hover:bg-white/10 hover:text-zinc-200
                        ${!hasChildren ? 'invisible pointer-events-none' : ''}
                    `}
                    onClick={onChevronClick}
                >
                    <span
                        className={`inline-block transition-transform ${expanded ? 'rotate-90' : ''}`}
                        aria-hidden
                    >
                        ›
                    </span>
                </button>
                <HierarchyIcon className={accent.icon} />
                <span className={`min-w-0 flex-1 truncate ${accent.labelClass}`}>{node.label}</span>
            </div>
            {hasChildren && expanded && (
                <div role="group">
                    {node.children.map((ch) => (
                        <OutlineRow
                            key={ch.id}
                            node={ch}
                            depth={depth + 1}
                            expandedIds={expandedIds}
                            selectedId={selectedId}
                            onToggleExpand={onToggleExpand}
                            onSelectFocus={onSelectFocus}
                        />
                    ))}
                </div>
            )}
        </div>
    );
});

export interface CanvasOutlinePanelProps {
    tree: OutlineTreeNode[];
    expandedIds: Set<string>;
    selectedId: string | null;
    setExpandedIds: React.Dispatch<React.SetStateAction<Set<string>>>;
    onFocusNode: (node: OutlineTreeNode) => void;
    /** Focus canvas branch on Start (same subtree as clicking Start on the canvas). */
    onGoHome?: () => void;
    /** Sidebar dock under project list vs. legacy rail beside canvas. */
    layout?: 'sidebar' | 'rail';
}

export const CanvasOutlinePanel: React.FC<CanvasOutlinePanelProps> = ({
    tree,
    expandedIds,
    selectedId,
    setExpandedIds,
    onFocusNode,
    onGoHome,
    layout = 'sidebar',
}) => {
    const expandableIds = useMemo(() => {
        const s = new Set<string>();
        const walk = (n: OutlineTreeNode) => {
            if (n.children.length > 0) s.add(n.id);
            n.children.forEach(walk);
        };
        tree.forEach(walk);
        return s;
    }, [tree]);

    const onToggleExpand = useCallback(
        (id: string) => {
            if (!expandableIds.has(id)) return;
            setExpandedIds((prev) => {
                const next = new Set(prev);
                if (next.has(id)) next.delete(id);
                else next.add(id);
                return next;
            });
        },
        [expandableIds, setExpandedIds]
    );

    const expandAll = useCallback(() => {
        setExpandedIds(new Set(expandableIds));
    }, [expandableIds, setExpandedIds]);

    const collapseAll = useCallback(() => {
        setExpandedIds(new Set());
    }, [setExpandedIds]);

    const allExpanded =
        expandableIds.size > 0 && [...expandableIds].every((id) => expandedIds.has(id));

    const toggleExpandCollapseAll = useCallback(() => {
        if (allExpanded) {
            collapseAll();
        } else {
            expandAll();
        }
    }, [allExpanded, collapseAll, expandAll]);

    const shellClass =
        layout === 'rail'
            ? 'flex h-full w-[min(100%,280px)] shrink-0 flex-col border-r border-white/10 bg-[#0f0d18]'
            : 'flex h-full min-h-0 w-full flex-col bg-white/[0.07]';

    return (
        <aside className={shellClass} aria-label="Requirements outline">
            <header className="flex items-center justify-between gap-2 border-b border-white/10 px-2 py-2">
                <span className="truncate pl-1 text-[11px] font-semibold uppercase tracking-wide text-zinc-300/90">
                    Outline
                </span>
                <div className="flex shrink-0 items-center gap-0.5">
                    {onGoHome ? (
                        <button
                            type="button"
                            title="Focus canvas on Start"
                            aria-label="Focus canvas on Start"
                            disabled={tree.length === 0}
                            className="rounded p-1.5 text-zinc-400 hover:bg-white/10 hover:text-zinc-200 disabled:pointer-events-none disabled:opacity-30"
                            onClick={onGoHome}
                        >
                            <svg
                                className="h-4 w-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                aria-hidden
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                                />
                            </svg>
                        </button>
                    ) : null}
                    <button
                        type="button"
                        title={allExpanded ? 'Collapse all' : 'Expand all'}
                        aria-label={allExpanded ? 'Collapse all' : 'Expand all'}
                        disabled={expandableIds.size === 0}
                        className="rounded p-1.5 text-zinc-400 hover:bg-white/10 hover:text-zinc-200 disabled:pointer-events-none disabled:opacity-30"
                        onClick={toggleExpandCollapseAll}
                    >
                        {allExpanded ? (
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M5 15l7-7 7 7"
                                />
                            </svg>
                        ) : (
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 9l-7 7-7-7"
                                />
                            </svg>
                        )}
                    </button>
                </div>
            </header>
            <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-1 py-2" role="tree">
                {tree.length === 0 ? (
                    <p className="px-2 py-4 text-center text-xs text-zinc-500">No requirements on canvas</p>
                ) : (
                    tree.map((n) => (
                        <OutlineRow
                            key={n.id}
                            node={n}
                            depth={0}
                            expandedIds={expandedIds}
                            selectedId={selectedId}
                            onToggleExpand={onToggleExpand}
                            onSelectFocus={onFocusNode}
                        />
                    ))
                )}
            </div>
        </aside>
    );
};
